// ═══════════════════════════════════════════════════════════
// Перегляд і друк документа .docx у браузері (24.10).
//
// Файл тягнемо через наш API (прямий fetch за посиланням сховища браузер
// не пустить — CORS), малює docx-preview — бібліотека вантажиться лише
// коли документ відкривають. Друк — у прихованому iframe, щоб на папері
// був тільки документ. Вигляд близький до Word, але не піксель у піксель;
// для точного друку — «Завантажити» і відкрити у Word.
// ═══════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from 'react';
import { apiFetchBlob } from '../../services/api';
import { useCloseOnEscape } from './docFiles';

const loadLib = () => import('docx-preview');

export async function printDocx(blob) {
  const { renderAsync } = await loadLib();
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument;
  idoc.open();
  idoc.write('<!doctype html><html><head><meta charset="utf-8"><style>@page{margin:0}body{margin:0}.docx-wrapper{background:none!important;padding:0!important}.docx-wrapper>section.docx{box-shadow:none!important;margin:0 auto!important}</style></head><body></body></html>');
  idoc.close();
  await renderAsync(blob, idoc.body, idoc.head, { inWrapper: true, breakPages: true, ignoreLastRenderedPageBreak: true });
  const cleanup = () => setTimeout(() => iframe.remove(), 500);
  iframe.contentWindow.addEventListener('afterprint', cleanup);
  iframe.contentWindow.focus();
  iframe.contentWindow.print();
  setTimeout(cleanup, 60_000);
}

export default function DocxPreviewModal({ doc, onClose }) {
  const box = useRef(null);
  const [blob, setBlob] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | error
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = await apiFetchBlob(`/api/document-templates?action=document-file&id=${encodeURIComponent(doc.id)}`);
        if (cancelled) return;
        setBlob(b);
        const { renderAsync } = await loadLib();
        if (cancelled || !box.current) return;
        box.current.innerHTML = '';
        await renderAsync(b, box.current, null, { inWrapper: true, breakPages: true, ignoreLastRenderedPageBreak: true });
        if (!cancelled) setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id]);

  useCloseOnEscape(onClose);

  const print = async () => {
    if (!blob || printing) return;
    setPrinting(true);
    try { await printDocx(blob); } finally { setPrinting(false); }
  };

  return (
    <div className="modal-overlay docx-preview-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal docx-preview-modal" role="dialog" aria-modal="true" aria-label={doc.name}>
        <div className="modal-header">
          <h2 className="docx-preview-title">{doc.name}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Закрити">✕</button>
        </div>
        <div className="docx-preview-toolbar">
          <button type="button" className="btn btn-primary btn-sm" onClick={print} disabled={state !== 'ready' || printing}>
            🖨 {printing ? 'Готуємо…' : 'Друк / PDF'}
          </button>
          {doc.file?.url && (
            <a className="btn btn-secondary btn-sm" href={doc.file.url} download={doc.file.name} target="_blank" rel="noreferrer">⬇ Завантажити .docx</a>
          )}
          <span className="docx-preview-hint">Для PDF у вікні друку виберіть «Зберегти як PDF».</span>
        </div>
        <div className="docx-preview-body">
          {state === 'loading' && <p className="api-keys-muted">Відкриваємо документ…</p>}
          {state === 'error' && <p className="tel-err">Не вдалося відкрити документ. Завантажте файл і відкрийте у Word.</p>}
          <div ref={box} className="docx-preview-box" />
        </div>
      </div>
    </div>
  );
}
